import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  BarChart3,
  Loader2,
  MapPin,
  RefreshCw,
  ShoppingBag,
} from 'lucide-react'
import { fetchStores, type StoreOption } from '../api/auth'
import { fetchSalesAnalysis } from '../api/salesAnalysis'
import { SalesAnalysisChart } from '../components/SalesAnalysisChart'
import { DashboardGroupSubMenu } from '../components/DashboardGroupSubMenu'
import { PeriodControls } from '../components/PeriodControls'
import { ShopPills } from '../components/ShopPills'
import { useAuth } from '../context/AuthContext'
import {
  aggregateDailySales,
  availableGroupings,
  daysInRange,
  defaultGrouping,
  summarizeSales,
  toSalesSeries,
  SALES_GROUPINGS,
  SALES_METRICS,
  type SalesGrouping,
  type SalesMetric,
  type SalesScale,
  type SalesShopRef,
} from '../dashboard/salesAnalysis'
import { SHOP_REGIONS } from '../dashboard/regions'
import { canSelectShops, entitledStores } from '../lib/access'
import {
  formatCurrency,
  formatNumber,
  formatShortTimestamp,
} from '../lib/format'
import { formatRange, matchingPreset, rangePresets, type DateRange } from '../lib/ranges'

const BUTTON_BASE =
  'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors'
const BUTTON_ACTIVE =
  'border-salsa-500 bg-salsa-500/10 text-salsa-700 dark:border-salsa-500/50 dark:text-salsa-300'
const BUTTON_IDLE =
  'border-slate-200 text-slate-600 hover:border-salsa-300 hover:text-salsa-700 dark:border-white/10 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200'

function metricLabel(metric: SalesMetric): string {
  if (metric === 'orders') return 'Order count'
  if (metric === 'aov') return 'AOV'
  if (metric === 'refunds') return 'Refunds'
  return 'Sales'
}

function joinStoreCodes(codes: string[]): string {
  if (codes.length <= 1) return codes[0] ?? ''
  if (codes.length === 2) return `${codes[0]} and ${codes[1]}`
  return `${codes.slice(0, -1).join(', ')} and ${codes[codes.length - 1]}`
}

function KpiCard({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint: string
}) {
  return (
    <article className="card p-3">
      <span className="kpi-label">{label}</span>
      <p className="mt-1 text-xl font-semibold tabular-nums tracking-tight text-slate-900 dark:text-white">
        {value}
      </p>
      <p className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400">{hint}</p>
    </article>
  )
}

/**
 * Sales Analysis — order-line-backed series.
 *
 * The page fetches one day series and one hour-of-day series per period/shop
 * selection. Changing grouping, metric, scale or channel mode is done on the
 * client; only period/shop changes or Refresh re-fetch. The response is mapped
 * onto the chart's fixed six-channel contract in `dashboard/salesAnalysis.ts`.
 * Refunds are shown separately from the already-netted sales total. An account
 * with exactly one shop opens on that shop (D-101); multi-shop accounts start
 * unselected and wait for an explicit choice.
 */
export function SalesAnalysisPage() {
  const { user } = useAuth()
  const now = useMemo(() => new Date(), [])
  const presets = useMemo(
    () => rangePresets(now).filter((preset) => preset.label !== 'All time'),
    [now],
  )
  const [range, setRange] = useState<DateRange>(() => {
    const today = rangePresets(new Date()).find((preset) => preset.label === 'Today')
    return today
      ? { from: today.from, to: today.to, preset: 'Today' }
      : { from: presets[0].from, to: presets[0].to }
  })
  const [pickedIds, setPickedIds] = useState<number[]>([])
  const [metric, setMetric] = useState<SalesMetric>('sales')
  const [grouping, setGrouping] = useState<SalesGrouping>(() =>
    defaultGrouping(daysInRange(range.from, range.to)),
  )
  const [scale, setScale] = useState<SalesScale>('total')
  const [byChannel, setByChannel] = useState(false)
  const autoSelectedRef = useRef(false)

  const storesQuery = useQuery({
    queryKey: ['stores'],
    queryFn: fetchStores,
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  })
  const catalogue: StoreOption[] = storesQuery.data ?? []
  const visibleStores = useMemo(() => entitledStores(user, catalogue), [user, catalogue])
  const visibleIds = useMemo(() => visibleStores.map((store) => store.id), [visibleStores])

  // A one-shop account has nothing to choose, so open on its only shop instead
  // of asking for a pointless click (D-101). Multi-shop accounts keep the
  // explicit no-selection default, and a later "None" click is not overridden.
  useEffect(() => {
    if (autoSelectedRef.current || visibleIds.length !== 1) return
    autoSelectedRef.current = true
    setPickedIds([visibleIds[0]])
  }, [visibleIds])

  const canPick = canSelectShops(user)
  // District managers only need All/None; other regional shortcuts are out of
  // their scope and would otherwise render disabled.
  const showRegionQuickSelect = canPick && user?.role !== 'district_manager'

  const selectedIds = useMemo(() => {
    const base = pickedIds
    const allowed = new Set(visibleIds)
    return base.filter((id) => allowed.has(id))
  }, [pickedIds, visibleIds])

  const selectedStores = useMemo<SalesShopRef[]>(() => {
    return visibleStores
      .filter((store) => selectedIds.includes(store.id))
      .map((store) => ({ id: store.id, code: store.code }))
  }, [visibleStores, selectedIds])

  const days = daysInRange(range.from, range.to)
  const available = useMemo(() => availableGroupings(days), [days])
  const preferredGrouping = defaultGrouping(days)

  useEffect(() => {
    if (!available.includes(grouping)) setGrouping(preferredGrouping)
  }, [available, grouping, preferredGrouping])

  const salesBaseKey = [
    'butlaroo-sales-analysis',
    range.from,
    range.to,
    selectedStores.map((store) => store.id).join(','),
  ]
  const queryConfig = (requestedGrouping: SalesGrouping) => ({
    queryKey: [...salesBaseKey, requestedGrouping],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchSalesAnalysis({
        from: range.from,
        to: range.to,
        storeIds: selectedStores.map((store) => store.id),
        grouping: requestedGrouping,
        signal,
      }),
    enabled: selectedStores.length > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1 as const,
  })
  const hourlyQuery = useQuery(queryConfig('hour'))
  const dailyQuery = useQuery(queryConfig('day'))
  const salesQuery = grouping === 'hour' ? hourlyQuery : dailyQuery
  const data = useMemo(
    () => (salesQuery.data ? aggregateDailySales(salesQuery.data, grouping) : undefined),
    [salesQuery.data, grouping],
  )
  const isInitialLoading =
    selectedStores.length > 0 && (hourlyQuery.isPending || dailyQuery.isPending)
  const isRefreshing =
    (hourlyQuery.isFetching || dailyQuery.isFetching) && !isInitialLoading
  const refundsAvailable = data?.refunds_available ?? false
  const channelsAvailable = data?.channels_available ?? false

  useEffect(() => {
    if (data && !data.refunds_available && metric === 'refunds') setMetric('sales')
  }, [data, metric])

  useEffect(() => {
    if (data && !data.channels_available && byChannel) setByChannel(false)
  }, [data, byChannel])

  const series = useMemo(() => (data ? toSalesSeries(data) : null), [data])
  const summary = useMemo(
    () => (series ? summarizeSales(series, metric, grouping, scale) : null),
    [series, metric, grouping, scale],
  )
  const activePreset = matchingPreset(range, now)
  const selectedStoreLabel =
    selectedStores.length === 1
      ? visibleStores.find((store) => store.id === selectedStores[0].id)?.label
      : undefined
  const selectedLabel =
    selectedStores.length === 1
      ? (selectedStoreLabel ?? selectedStores[0].code)
      : selectedStores.length === 0
        ? 'No shop selected'
        : `${selectedStores.length} shops`

  const toggleStore = (id: number) => {
    setPickedIds((current) => {
      const base = current ?? visibleIds
      return base.includes(id) ? base.filter((value) => value !== id) : [...base, id]
    })
  }
  const selectAll = () => setPickedIds(visibleIds)
  const clearSelection = () => setPickedIds([])
  const refreshAll = () => {
    void Promise.all([hourlyQuery.refetch(), dailyQuery.refetch()])
  }

  const regionOptions = useMemo(
    () =>
      SHOP_REGIONS.map((region) => {
        const codes = new Set(region.storeCodes)
        return {
          ...region,
          ids: visibleStores.filter((store) => codes.has(store.code)).map((store) => store.id),
        }
      }),
    [visibleStores],
  )

  const allSelected = visibleIds.length > 0 && selectedIds.length === visibleIds.length
  const noneSelected = selectedIds.length === 0
  const groupingLabel =
    SALES_GROUPINGS.find((option) => option.key === grouping)?.label ?? 'Period'
  const refundRate =
    summary && summary.sales > 0 ? (summary.refunds / summary.sales) * 100 : 0
  const unavailableStores = data?.stores.filter((store) => !store.available) ?? []
  const credentialMissingStores = unavailableStores.filter(
    (store) => store.error_code === 'butlaroo_credentials_missing',
  )
  const otherUnavailableStores = unavailableStores.filter(
    (store) => store.error_code !== 'butlaroo_credentials_missing',
  )
  const missingCredentialCodes = credentialMissingStores.map((store) => store.store_code)
  const missingCredentialCodeSet = new Set(missingCredentialCodes)
  const salesWarnings = (data?.warnings ?? []).filter((warning) => {
    if (/Skipped \d+ non-paid or malformed order/.test(warning)) return false
    const scoped = warning.match(/^(\d{3})\s*:/)
    return !(
      scoped &&
      missingCredentialCodeSet.has(scoped[1]) &&
      /No Butlaroo credentials are configured/.test(warning)
    )
  })
  const hasUnmappedChannels = (data?.unmapped_sales_eur ?? 0) > 0
  const salesError =
    salesQuery.error instanceof Error
      ? salesQuery.error.message
      : 'The sales series could not be loaded.'
  const showEmptyState =
    Boolean(series) &&
    !isInitialLoading &&
    !salesQuery.isError &&
    series !== null &&
    series.totals.sales === 0 &&
    series.totals.orders === 0 &&
    series.totals.refunds === 0

  return (
    <div id="sales-analysis-panel" className="space-y-3">
      <DashboardGroupSubMenu
        activeKey="sales_analysis"
        fallbackGroupKey="insights"
        panelId="sales-analysis-panel"
      />

      <section className="card space-y-3 p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-blue-700 text-white shadow-sm"
              aria-hidden="true"
            >
              <BarChart3 className="h-4 w-4" />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                Sales Analysis
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Sales, orders, AOV and channel mix by period and grouping.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isRefreshing && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                Refreshing
              </span>
            )}
            <button
              type="button"
              onClick={refreshAll}
              disabled={selectedStores.length === 0 || isRefreshing}
              className={`${BUTTON_BASE} ${BUTTON_IDLE} inline-flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-40`}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
              Refresh
            </button>
          </div>
        </div>

        <PeriodControls value={range} onChange={setRange} presets={presets} now={now} />

        {canPick && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Region
            </span>
            <button
              type="button"
              onClick={selectAll}
              disabled={visibleIds.length === 0}
              aria-pressed={allSelected}
              className={`${BUTTON_BASE} ${allSelected ? BUTTON_ACTIVE : BUTTON_IDLE} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              All
            </button>
            <button
              type="button"
              onClick={clearSelection}
              disabled={noneSelected}
              aria-pressed={noneSelected}
              className={`${BUTTON_BASE} ${noneSelected ? BUTTON_ACTIVE : BUTTON_IDLE} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              None
            </button>
            {showRegionQuickSelect &&
              regionOptions.map((region) => {
                const active =
                  region.ids.length > 0 &&
                  region.ids.length === selectedIds.length &&
                  region.ids.every((id) => selectedIds.includes(id))
                return (
                  <button
                    key={region.key}
                    type="button"
                    onClick={() => setPickedIds(region.ids)}
                    disabled={region.ids.length === 0}
                    aria-pressed={active}
                    title={`${region.name} · ${region.ids.length} shops (${region.storeCodes.join(', ')})`}
                    className={`${BUTTON_BASE} ${active ? BUTTON_ACTIVE : BUTTON_IDLE} disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    {region.name}
                  </button>
                )
              })}
          </div>
        )}

        {canPick ? (
          <ShopPills
            stores={visibleStores}
            selectedIds={selectedIds}
            onToggle={toggleStore}
            onSelectAll={selectAll}
            onClear={clearSelection}
            showControls={false}
          />
        ) : selectedStores.length === 0 && visibleStores[0] ? (
          <button
            type="button"
            onClick={() => setPickedIds([visibleStores[0].id])}
            className={`${BUTTON_BASE} ${BUTTON_IDLE} inline-flex items-center gap-1`}
          >
            <MapPin className="h-3 w-3" aria-hidden="true" />
            Use {visibleStores[0].label}
          </button>
        ) : (
          <p className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
            <MapPin className="h-3 w-3" aria-hidden="true" />
            {selectedLabel}
          </p>
        )}

        {storesQuery.isError && (
          <p className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            Could not load the shop list.
            <button
              type="button"
              onClick={() => void storesQuery.refetch()}
              className="font-semibold underline underline-offset-2"
            >
              Retry
            </button>
          </p>
        )}
      </section>

      {selectedStores.length === 0 ? (
        <section className="card flex items-center gap-2 p-4 text-sm text-slate-500 dark:text-slate-400">
          <ShoppingBag className="h-4 w-4" aria-hidden="true" />
          Choose at least one shop to build the sales analysis.
        </section>
      ) : (
        <>
          {salesQuery.isError && (
            <div className="flex flex-wrap items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-xs text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <strong>Sales are unavailable.</strong> {salesError}
                {series ? ' Showing the last successful data.' : ''}
              </span>
              <button
                type="button"
                onClick={refreshAll}
                disabled={isRefreshing}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold hover:bg-amber-500/10 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                Retry
              </button>
            </div>
          )}

          {data &&
            (unavailableStores.length > 0 ||
              salesWarnings.length > 0 ||
              hasUnmappedChannels) && (
              <div className="flex items-start gap-2 rounded-2xl border border-amber-200/80 bg-amber-50/60 px-4 py-3 text-[11px] leading-relaxed text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <div className="space-y-1">
                  {credentialMissingStores.length > 0 && (
                    <p>
                      Butlaroo credentials are not configured for{' '}
                      {joinStoreCodes(missingCredentialCodes)}.
                    </p>
                  )}
                  {otherUnavailableStores.map((store) => (
                    <p key={`unavailable-${store.store_id}`}>
                      <strong>#{store.store_code}</strong> is unavailable
                      {store.error_message ? `: ${store.error_message}` : '.'}
                    </p>
                  ))}
                  {salesWarnings.map((warning, index) => (
                    <p key={`${warning}-${index}`}>{warning}</p>
                  ))}
                  {hasUnmappedChannels && (
                    <p>
                      {formatCurrency(data.unmapped_sales_eur)} in paid sales
                      {data.unmapped_orders > 0
                        ? ` across ${formatNumber(data.unmapped_orders)} order(s)`
                        : ''}{' '}
                      could not be mapped to a known channel.
                    </p>
                  )}
                </div>
              </div>
            )}

          {isInitialLoading ? (
            <section className="card flex items-center gap-2 p-6 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading sales for {formatRange(range)}…
            </section>
          ) : series && summary ? (
            <>
              <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                <KpiCard
                  label="Sales"
                  value={formatCurrency(summary.sales)}
                  hint={`Total sales · ${formatRange(range)}`}
                />
                <KpiCard
                  label="Orders"
                  value={formatNumber(summary.orders / summary.divisor, 0)}
                  hint={
                    grouping === 'hour' && scale === 'average'
                      ? 'average per day'
                      : 'total for the period'
                  }
                />
                <KpiCard
                  label="AOV"
                  value={formatCurrency(summary.aov)}
                  hint="Revenue ÷ orders"
                />
                <KpiCard
                  label="Refunds"
                  value={
                    refundsAvailable
                      ? formatCurrency(summary.refunds / summary.divisor)
                      : '—'
                  }
                  hint={
                    refundsAvailable
                      ? `${formatNumber(refundRate, 1)}% of sales`
                      : 'Refund data is unavailable.'
                  }
                />
              </div>

              {showEmptyState ? (
                <section className="card flex flex-col items-start gap-2 p-6 text-sm text-slate-500 dark:text-slate-400">
                  <ShoppingBag className="h-4 w-4" aria-hidden="true" />
                  <p className="font-semibold text-slate-900 dark:text-white">
                    No paid sales in this period.
                  </p>
                  <p className="text-xs">
                    {selectedLabel} · {formatRange(range)} has no paid orders. Try another
                    period or shop.
                  </p>
                </section>
              ) : (
                <section className="card space-y-3 p-3">
            <header className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  {metricLabel(metric)} by {groupingLabel.toLowerCase()}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {selectedLabel} · {formatRange(range)}
                  {activePreset ? ` · ${activePreset}` : ''}
                </p>
              </div>
              <span className="badge bg-slate-500/15 text-slate-600 dark:text-slate-300">
                {series.points.length} buckets
              </span>
              {data && data.source !== 'butlaroo_orders' && (
                <span
                  className="badge bg-sky-500/15 text-sky-700 dark:text-sky-300"
                  title={
                    data.source === 'mixed'
                      ? 'VM Hub history plus live Butlaroo sales.'
                      : `The period ${data.start_date} to ${data.end_date} is VM Hub data, not live Butlaroo.`
                  }
                >
                  {data.source === 'mixed' ? 'Archive + Butlaroo' : 'VM Hub archive'}
                </span>
              )}
            </header>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Metric
                </span>
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Sales metric">
                  {SALES_METRICS.map((option) => {
                    const unavailable = option.key === 'refunds' && !refundsAvailable
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setMetric(option.key)}
                        disabled={unavailable}
                        aria-pressed={metric === option.key}
                        title={
                          unavailable
                            ? 'Refund data is unavailable for this shop.'
                            : option.label
                        }
                        className={`${BUTTON_BASE} ${
                          metric === option.key ? BUTTON_ACTIVE : BUTTON_IDLE
                        } disabled:cursor-not-allowed disabled:opacity-35`}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Group by
                </span>
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Sales grouping">
                  {SALES_GROUPINGS.map((option) => {
                    const enabled = available.includes(option.key)
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setGrouping(option.key)}
                        disabled={!enabled}
                        aria-pressed={grouping === option.key}
                        title={
                          enabled ? option.label : `Not available for a ${days}-day period`
                        }
                        className={`${BUTTON_BASE} ${
                          grouping === option.key ? BUTTON_ACTIVE : BUTTON_IDLE
                        } disabled:cursor-not-allowed disabled:opacity-35`}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={byChannel}
                  onClick={() => setByChannel((current) => !current)}
                  disabled={!channelsAvailable}
                  title={
                    channelsAvailable
                      ? 'Split the selected metric by channel'
                      : 'Channel data is not available for this response.'
                  }
                  className="ml-auto inline-flex items-center gap-2 rounded-full border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:border-white/20"
                >
                  <span
                    className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
                      byChannel ? 'bg-salsa-500' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                    aria-hidden="true"
                  >
                    <span
                      className={`inline-block h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${
                        byChannel ? 'translate-x-3.5' : 'translate-x-0.5'
                      }`}
                    />
                  </span>
                  By channel
                </button>
              </div>

              {grouping === 'hour' || grouping === 'weekday' ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Scale
                  </span>
                  <div className="flex gap-1.5" role="group" aria-label="Chart scale">
                    {(['total', 'average'] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setScale(option)}
                        aria-pressed={scale === option}
                        className={`${BUTTON_BASE} ${scale === option ? BUTTON_ACTIVE : BUTTON_IDLE}`}
                      >
                        {option === 'total' ? 'Total' : 'Average'}
                      </button>
                    ))}
                  </div>
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    {grouping === 'hour'
                      ? 'Average = per day across the selected shops'
                      : 'Average = per occurrence of each weekday'}
                  </span>
                </div>
              ) : null}
            </div>

            <SalesAnalysisChart
              points={series.points}
              metric={metric}
              grouping={grouping}
              scale={scale}
              days={series.days}
              byChannel={byChannel}
            />
                </section>
              )}

              <p className="px-1 text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">
                Butlaroo orders · {formatNumber(data?.order_rows ?? 0)} paid orders
                {data?.line_rows != null
                  ? ` · ${formatNumber(data.line_rows)} order lines`
                  : ''}
                {data?.fetched_at ? ` · updated ${formatShortTimestamp(data.fetched_at)}` : ''}.
                Hour and day grouping are always available; week/month/quarter unlock with longer
                ranges, and day of week is always available.
              </p>
            </>
          ) : null}
        </>
      )}
    </div>
  )
}
